import dayjs from 'dayjs';
import { CarryMember, CarryNotice, CarryResult } from '../types/coachCarry';
import { toSmartScheduleKst } from '../features/smartSchedule/date';

export type CarryCenterNames = { fromCenterName: string; targetName: string };

export const CARRY_FIXED_FEE_NOTICE = '이어받은 수강권은 판매가가 0원이라 비율(%) 수업료로는 0원이 돼요. 받는 센터 관리자에게 이어받은 수강권별로 고정 금액 수업료를 지정해 달라고 해 주세요.';

const SHARED_WARNING_CODES = ['TARGET_RATE_TABLE_MISSING', 'PAST_LESSON_CORRECTION_LOCKED'];
export const isSharedCarryWarning = (notice: CarryNotice) => SHARED_WARNING_CODES.includes(notice.code);
export const sharedCarryWarnings = (members: CarryMember[]) => Array.from(new Map(
    members.flatMap((member) => member.warnings).filter(isSharedCarryWarning).map((warning) => [warning.code, warning])
).values());

export const carryKoreanMessage = (message: string, centerNames: string[] = []) => {
    const translate = (text: string) => text.replace(/\bID\b/g, '식별 정보').replace(/\bApp\b/gi, '앱');
    const names = centerNames.filter(Boolean).sort((a, b) => b.length - a.length);
    const parts = names.length ? message.split(new RegExp(`(${names.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g')) : [message];
    const translated = parts.map((part) => names.includes(part) ? part : translate(part)).join('');
    const content = parts.filter((part) => !names.includes(part)).map(translate).join('');
    return /[가-힣]/.test(translated) && !/[a-zA-Z_]/.test(content.replace(/\bPT\b/g, '')) ? translated : null;
};

export const carryNoticeMessage = (notice: CarryNotice, { fromCenterName, targetName }: CarryCenterNames) => {
    const messages: Record<string, string> = {
        NO_ACTIVE_PT: '이 강사에게 받은 진행 중인 PT 수강권이 없어요. 회원의 수강권 정보를 확인해 주세요.',
        LEGACY_CENTERLESS_MEMBERSHIP: '센터가 지정되지 않은 예전 수강권이 있어요. 라포에 문의해 주세요.',
        SHARED_MEMBERSHIP: '공유 수강권은 아직 이어받을 수 없어요. 이 회원은 제외하고 선택해 주세요.',
        COMPANION_MEMBERSHIP: '1:2 수강권은 아직 이어받을 수 없어요. 이 회원은 제외하고 선택해 주세요.',
        HANDOFF_SOURCE: '이미 인계된 수강권이에요. 회원의 인계 기록을 확인해 주세요.',
        ALREADY_CARRIED: '이미 이어보낸 수강권이에요. 이어받기 결과를 확인해 주세요.',
        MEMBERSHIP_PAUSED: '일시정지 중인 수강권이에요. 먼저 정지를 해제해 주세요.',
        FUTURE_SCHEDULES: `예정된 수업이 있어요. ${fromCenterName}에서 먼저 취소하고 ${targetName}에서 다시 잡아 주세요.`,
        LEGACY_FUTURE_SCHEDULES: `${fromCenterName}에 수강권이 지정되지 않은 예정 수업이 있어요. 먼저 정리해 주세요.`,
        PENDING_REQUESTS: `${fromCenterName}에 대기 중인 수업 신청이 있어요. 먼저 승인하거나 거절해 주세요.`,
        TRANSFER_IN_PROGRESS: '진행 중이거나 결과 확인이 필요한 양도가 있어요. 양도 결과를 확인한 뒤 다시 진행해 주세요.',
        HANDOFF_IN_PROGRESS: '진행 중인 인계가 있어요. 인계를 마친 뒤 다시 해 주세요',
        SALES_REFUND_UNRESOLVED: '처리 중이거나 확인이 필요한 원매출 환불이 있어요. 매출 목록에서 환불 상태를 확인해 주세요.',
        PAYMENT_DELETE_IN_PROGRESS: '마무리되지 않은 결제내역 삭제가 있어요. 다시 삭제해 마무리해 주세요.',
        MEMBERSHIP_REFERENCE_CASE_MIXED: '수강권 번호 표기가 섞인 기록이 있어요. 라포에 문의해 주세요.',
        TOO_MANY_RECORDS: '수업·결제 기록이 너무 많아 자동으로 이어받을 수 없어요. 라포에 문의해 주세요.',
        RELATION_AMBIGUOUS: `${fromCenterName}에 이 강사와 회원의 연결이 여러 개 있어요. 라포에 문의해 주세요.`,
        FROM_RELATION_MISSING: `${fromCenterName}에 이 강사와 회원의 연결이 없어요. 회원의 담당 강사를 확인해 주세요.`,
        MEMBER_UNAVAILABLE: '삭제됐거나 탈퇴한 회원이에요. 이 회원은 제외하고 선택해 주세요.',
        UNPAID_REMAINS_AT_FROM_CENTER: `미수는 ${fromCenterName}에 남아요. ${fromCenterName}에서 받아 주세요.`,
        TARGET_RATE_TABLE_MISSING: `${targetName}에 이 강사의 요율표가 없어요. 받는 센터 관리자에게 요율표를 만들어 달라고 해 주세요.`,
        PAST_LESSON_CORRECTION_LOCKED: `이어받은 뒤에는 ${fromCenterName}의 지난 수업을 취소·복구할 수 없어요. 정정할 내용이 있으면 먼저 처리해 주세요.`,
    };
    if (Object.prototype.hasOwnProperty.call(messages, notice.code)) return messages[notice.code];
    return carryKoreanMessage(notice.message, [fromCenterName, targetName])
        || '회원 상태를 확인하지 못했어요. 라포에 문의해 주세요.';
};

export const carryAmount = (amount: number) => {
    const tenThousands = Math.floor(amount / 10000);
    const remainder = amount % 10000;
    return tenThousands > 0
        ? `${tenThousands.toLocaleString('ko-KR')}만${remainder ? ` ${remainder.toLocaleString('ko-KR')}원` : ' 원'}`
        : `${amount.toLocaleString('ko-KR')}원`;
};

export const carryDate = (value: string | null | undefined) =>
    value && dayjs(value).isValid() ? toSmartScheduleKst(value).format('M월 D일') : '';

export const carryMembershipSummary = (membership: CarryMember['memberships'][number], targetName: string) =>
    `${membership.name} · 남은 ${membership.remainingSessions.toLocaleString('ko-KR')}회 · ${targetName}에서 ${membership.newStartSessionValue.toLocaleString('ko-KR')}회차부터 · 만료 ${membership.expirationDate ? carryDate(membership.expirationDate) || '정보 없음' : '없음'}`;

export type CarryMemberDetails = { memberships: { text: string; unpaid: string; unpaidAmount?: number; title?: string }[]; warnings: string[] };
export const carryMemberDetails = (member: CarryMember, names: CarryCenterNames): CarryMemberDetails => ({
    memberships: member.memberships.map((membership) => ({
        text: carryMembershipSummary(membership, names.targetName),
        title: membership.expirationDate ? `만료 ${carryDate(membership.expirationDate) || '정보 없음'}` : '만료 없음',
        unpaid: membership.unpaidAmount > 0 ? `미수 ${carryAmount(membership.unpaidAmount)}(${names.fromCenterName}에서 받아요)` : '',
        unpaidAmount: membership.unpaidAmount,
    })),
    warnings: Array.from(new Set(member.warnings.filter((warning) => !isSharedCarryWarning(warning)
        && warning.code !== 'UNPAID_REMAINS_AT_FROM_CENTER')
        .map((warning) => carryNoticeMessage(warning, names)))),
});

export const carryUnpaidTotal = (results: CarryResult[]) => {
    const amounts = results.filter((result) => ['COMMITTED', 'REPLAYED'].includes(result.outcome))
        .map((result) => result.result?.unpaidAmountAtFromCenter);
    // 완료 응답의 원금만 합산한다. 미리보기나 표시 문구로 금액을 추측하지 않는다.
    if (amounts.some((amount) => amount === undefined || !Number.isSafeInteger(amount) || amount < 0)) return null;
    const total = amounts.reduce<number>((sum, amount) => sum + (amount ?? 0), 0);
    return Number.isSafeInteger(total) ? total : null;
};

/** 기존 안내의 사실과 다음 행동을 두 줄로 나눠요. 알 수 없는 서버 문구는 그대로 둬요. */
export const carryMessageLines = (message: string) => {
    const match = message.match(/^(.*?(?:어요|예요|이에요|해요|돼요|아요))\.\s+([\s\S]+)$/);
    return match ? { issue: `${match[1]}.`, action: match[2] } : { issue: message, action: '' };
};
